import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, ButtonGroup, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { abrirPDF } from "../../../../utils/abrirPDF";

// Corte Diario (Menú Ventas, permiso menu_admin): ventas del rango con su forma de pago,
// totales por forma de pago, comisión de cada vendedor (su % sobre la venta sin IVA, seguro
// ni 3.5% de tarjeta), gastos del módulo de Gastos y piezas por departamento.
// Efectivo a entregar = efectivo − comisiones − gastos.
const URL_ESTADISTICAS =
  process.env.REACT_APP_URL_ESTADISTICAS ||
  String(process.env.REACT_APP_URL_LOGS || "").replace(/logs\/?$/, "estadisticas");
const URL_SURTIDOS = process.env.REACT_APP_URL_SURTIDOS;
const URL_COLABORADORES = process.env.REACT_APP_URL_COLABORADORES;

const FORMAS = ["Efectivo", "Tarjeta", "Transferencia", "Cheque", "Crédito"];
const ETIQUETA_FORMA = { "Crédito": "Crédito (Documentos)" };

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const num = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);
const dmy = (f) => (f ? `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(2, 4)}` : "");
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });

function hoyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const tarjeta = { border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", background: "#fff" };
const titulo = { fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 };

function CorteDiarioVentas() {
  const { user } = useContext(AuthContext);
  const [desde, setDesde] = useState(hoyLocal());
  const [hasta, setHasta] = useState(hoyLocal());
  const [corte, setCorte] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [porcs, setPorcs] = useState({}); // % en edición por vendedor

  const cargar = useCallback(() => {
    if (!desde || !hasta) return;
    setCargando(true);
    axios
      .get(`${URL_ESTADISTICAS}/corteDiario`, { params: { desde, hasta }, headers: headers() })
      .then((r) => {
        setCorte(r.data);
        setPorcs({});
      })
      .catch((e) => {
        Swal.fire("Error", (e.response && e.response.data && e.response.data.message) || "No se pudo cargar el corte", "error");
      })
      .finally(() => setCargando(false));
  }, [desde, hasta]);

  useEffect(() => {
    if (user.menu_admin) cargar();
  }, [cargar, user.menu_admin]);

  async function cambiarForma(nota, forma) {
    if (forma === nota.forma && !nota.sinForma) return;
    try {
      await axios.patch(`${URL_SURTIDOS}/${nota._id}`, { formaPago: forma }, { headers: headers() });
      cargar();
    } catch (e) {
      Swal.fire("Error", "No se pudo cambiar la forma de pago", "error");
    }
  }

  async function guardarPorc(c) {
    const v = porcs[c._id];
    if (v === undefined) return;
    const p = parseFloat(v);
    if (isNaN(p) || p < 0 || p > 100) {
      Swal.fire("Revisa el %", "Debe ser un número entre 0 y 100", "warning");
      return;
    }
    if (p === c.porc) return;
    try {
      await axios.patch(`${URL_COLABORADORES}/${c._id}`, { porcComision: p }, { headers: headers() });
      cargar();
    } catch (e) {
      Swal.fire("Error", "No se pudo guardar el % de comisión", "error");
    }
  }

  const rango = corte ? (corte.desde === corte.hasta ? dmy(corte.desde) : `${dmy(corte.desde)} al ${dmy(corte.hasta)}`) : "";

  function PDF() {
    if (!corte) return;
    const doc = new jsPDF();
    const ancho = doc.internal.pageSize.getWidth();
    doc.setFontSize(14);
    doc.text(`CORTE DEL DÍA ${dmy(corte.desde)} AL ${dmy(corte.hasta)}`, ancho / 2, 15, { align: "center" });
    doc.autoTable({
      startY: 22,
      theme: "plain",
      tableWidth: 110,
      styles: { fontSize: 10, cellPadding: 1 },
      columnStyles: { 1: { halign: "right" } },
      body: [
        [{ content: "Venta Total", styles: { fontStyle: "bold" } }, { content: fmt(corte.ventaTotal), styles: { fontStyle: "bold" } }],
        ...FORMAS.map((f) => [ETIQUETA_FORMA[f] || f, fmt(corte.formas[f])]),
        ["Efectivo", fmt(corte.formas.Efectivo)],
        ["− Comisiones", fmt(corte.totalComisiones)],
        ["− Gastos", fmt(corte.totalGastos)],
        [{ content: "Efectivo a entregar", styles: { fontStyle: "bold" } }, { content: fmt(corte.efectivoNeto), styles: { fontStyle: "bold" } }],
      ],
    });
    doc.setFontSize(12);
    doc.text("VENTAS", ancho / 2, doc.lastAutoTable.finalY + 8, { align: "center" });
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 11,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
      columnStyles: { 4: { halign: "right" } },
      head: [["Nota", "Cliente", "Vendedor", "Hora", "Importe", "Pago"]],
      body: corte.notas.map((n) => [n.numero, n.cliente, n.vendedores.join(", "), n.hora, fmt(n.total), n.forma]),
      foot: [["", "Totales", "", "", fmt(corte.ventaTotal), ""]],
      footStyles: { fillColor: [241, 245, 249], textColor: 20, halign: "right" },
    });
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 6,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
      head: [["Departamento", "Vendidos", "Devueltos", "Diferencia"]],
      body: corte.departamentos.map((d) => [d.departamento, num(d.vendidos), num(d.devueltos), num(d.diferencia)]),
      foot: [
        ["Total de artículos", num(corte.piezas.vendidos), num(corte.piezas.devueltos), num(corte.piezas.diferencia)],
        ["Precio promedio por pieza", "", "", fmt(corte.piezas.precioPromedio)],
      ],
      footStyles: { fillColor: [241, 245, 249], textColor: 20, halign: "right" },
    });
    if (corte.gastos.length) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 6,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 23, 42] },
        columnStyles: { 3: { halign: "right" } },
        head: [["Fecha", "Concepto", "Descripción", "Importe"]],
        body: corte.gastos.map((g) => [dmy(g.fecha), g.concepto, g.descripcion, fmt(g.total)]),
        foot: [["", "", "Total gastos", fmt(corte.totalGastos)]],
        footStyles: { fillColor: [241, 245, 249], textColor: 20, halign: "right" },
      });
    }
    // Comisiones por vendedor (como el reporte de comisiones pagadas)
    doc.addPage();
    doc.setFontSize(14);
    doc.text(`REPORTE COMISIONES POR VENDEDOR`, ancho / 2, 15, { align: "center" });
    doc.setFontSize(11);
    doc.text(`DEL ${dmy(corte.desde)} AL ${dmy(corte.hasta)}`, ancho / 2, 21, { align: "center" });
    const cuerpo = [];
    corte.comisiones.forEach((c) => {
      c.notas.forEach((n) =>
        cuerpo.push([dmy(n.fecha), n.numero, `${c.vendedor} (${num(c.porc)}%)`, n.cliente, fmt(n.base), fmt(n.importe)])
      );
      cuerpo.push([
        { content: `Total ${c.vendedor}`, colSpan: 5, styles: { halign: "right", fontStyle: "bold" } },
        { content: fmt(c.importe), styles: { fontStyle: "bold", halign: "right" } },
      ]);
    });
    doc.autoTable({
      startY: 26,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
      columnStyles: { 4: { halign: "right" }, 5: { halign: "right" } },
      head: [["Fecha", "Nota", "Vendedor", "Cliente", "Venta sin IVA", "Comisión"]],
      body: cuerpo,
      foot: [["", "", "", "", "Total comisiones", fmt(corte.totalComisiones)]],
      footStyles: { fillColor: [241, 245, 249], textColor: 20, halign: "right" },
    });
    abrirPDF(doc);
  }

  function excel() {
    if (!corte) return;
    const notas = corte.notas.map((n) => ({
      Nota: n.numero,
      Fecha: n.fecha,
      Hora: n.hora,
      Cliente: n.cliente,
      Vendedor: n.vendedores.join(", "),
      Importe: n.total,
      Pago: n.forma,
    }));
    const comisiones = corte.comisiones.flatMap((c) =>
      c.notas.map((n) => ({
        Vendedor: c.vendedor,
        Porcentaje: c.porc,
        Fecha: n.fecha,
        Nota: n.numero,
        Cliente: n.cliente,
        VentaSinIVA: n.base,
        Comision: n.importe,
      }))
    );
    const deps = corte.departamentos.map((d) => ({
      Departamento: d.departamento,
      Vendidos: d.vendidos,
      Devueltos: d.devueltos,
      Diferencia: d.diferencia,
    }));
    const hoja = (data, nombre) => {
      const cols = data.length ? Object.keys(data[0]) : ["Sin datos"];
      return { sheetData: data, sheetName: nombre, sheetFilter: cols, sheetHeader: cols };
    };
    new ExportJsonExcel({
      fileName: `CorteDiario_${corte.desde}_${corte.hasta}`,
      datas: [hoja(notas, "Ventas"), hoja(comisiones, "Comisiones"), hoja(deps, "Departamentos")],
    }).saveExcel();
  }

  if (!user.menu_admin) return <Header />;

  const sinForma = corte ? corte.notas.filter((n) => n.sinForma).length : 0;

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card col-12">
        <Row>
          <Col md={8}>
            <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
              Regresar
            </Button>
          </Col>
          <Col sm={4}>
            <ButtonGroup id="logoutBoton">
              <Button size="sm" className="btn" color="danger" onClick={PDF} disabled={!corte}>
                PDF <i className="far fa-file-pdf"></i>
              </Button>
              <Button size="sm" className="btn" color="primary" onClick={excel} disabled={!corte}>
                Excel <i className="far fa-file-excel"></i>
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <h3 align="center">Corte Diario</h3>
        <Row className="justify-content-center" style={{ alignItems: "flex-end" }}>
          <Col md={2}>
            <Label className="mr-sm-2">Desde</Label>
            <Input bsSize="sm" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </Col>
          <Col md={2}>
            <Label className="mr-sm-2">Hasta</Label>
            <Input bsSize="sm" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </Col>
          <Col md={2}>
            <Button size="sm" color="info" onClick={() => { const h = hoyLocal(); setDesde(h); setHasta(h); }}>
              Hoy
            </Button>{" "}
            {cargando && <Spinner size="sm" color="primary" />}
          </Col>
        </Row>
        <br />

        {corte && (
          <>
            <p align="center" style={{ fontWeight: 600, marginBottom: 12 }}>
              Corte del {rango}
            </p>

            {/* Resumen por forma de pago y efectivo a entregar */}
            <Row>
              <Col md={7}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                  <div style={{ ...tarjeta, background: "#0f172a", color: "#fff", borderColor: "#0f172a" }}>
                    <div style={{ ...titulo, color: "#cbd5e1" }}>Venta total</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(corte.ventaTotal)}</div>
                  </div>
                  {FORMAS.map((f) => (
                    <div key={f} style={tarjeta}>
                      <div style={titulo}>{ETIQUETA_FORMA[f] || f}</div>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{fmt(corte.formas[f])}</div>
                    </div>
                  ))}
                </div>
              </Col>
              <Col md={5}>
                <div style={{ ...tarjeta, borderColor: "#15803d" }}>
                  <div style={{ ...titulo, marginBottom: 6 }}>Caja</div>
                  <Table size="sm" borderless style={{ marginBottom: 0 }}>
                    <tbody>
                      <tr>
                        <td>Efectivo</td>
                        <td align="right">{fmt(corte.formas.Efectivo)}</td>
                      </tr>
                      <tr>
                        <td>− Comisiones</td>
                        <td align="right" style={{ color: "#b91c1c" }}>{fmt(corte.totalComisiones)}</td>
                      </tr>
                      <tr>
                        <td>− Gastos</td>
                        <td align="right" style={{ color: "#b91c1c" }}>{fmt(corte.totalGastos)}</td>
                      </tr>
                      <tr style={{ borderTop: "2px solid #0f172a", fontWeight: 700, fontSize: 17 }}>
                        <td>Efectivo a entregar</td>
                        <td align="right" style={{ color: corte.efectivoNeto < 0 ? "#b91c1c" : "#15803d" }}>
                          {fmt(corte.efectivoNeto)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  <small style={{ color: "#64748b" }}>
                    La comisión no toma en cuenta el IVA, el seguro ni el 3.5% de tarjeta.
                  </small>
                </div>
              </Col>
            </Row>
            <br />

            {/* Ventas */}
            <h4 align="center">Ventas</h4>
            {sinForma > 0 && (
              <p align="center" style={{ color: "#b45309", marginTop: -4 }}>
                {sinForma} nota(s) sin forma de pago capturada (se tomaron como Tarjeta si se marcó, si no como
                Crédito). Puedes corregirla en la columna Pago.
              </p>
            )}
            <Table size="sm" striped hover responsive>
              <thead>
                <tr>
                  <th>Nota</th>
                  <th>Cliente</th>
                  <th>Vendedor</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th style={{ textAlign: "right" }}>Importe</th>
                  <th>Pago</th>
                </tr>
              </thead>
              <tbody>
                {corte.notas.map((n) => (
                  <tr key={n._id}>
                    <td>{n.numero}</td>
                    <td>{n.cliente}</td>
                    <td>{n.vendedores.join(", ")}</td>
                    <td>{dmy(n.fecha)}</td>
                    <td>{n.hora}</td>
                    <td align="right" style={{ color: n.total < 0 ? "#b91c1c" : undefined }}>{fmt(n.total)}</td>
                    <td style={{ minWidth: 150 }}>
                      <Input
                        bsSize="sm"
                        type="select"
                        value={n.forma}
                        onChange={(e) => cambiarForma(n, e.target.value)}
                        style={n.sinForma ? { borderColor: "#f59e0b", background: "#fffbeb" } : undefined}
                        title={n.sinForma ? "Sin capturar: valor supuesto" : ""}
                      >
                        {FORMAS.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                ))}
                {!corte.notas.length && (
                  <tr>
                    <td colSpan={7} align="center">
                      Sin ventas en el periodo
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td colSpan={5} align="right">
                    Totales ({corte.notas.length} notas)
                  </td>
                  <td align="right">{fmt(corte.ventaTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
            <br />

            {/* Comisiones por vendedor */}
            <h4 align="center">Comisiones por vendedor</h4>
            <p align="center" style={{ color: "#64748b", marginTop: -4 }}>
              El % se guarda en el vendedor y se usa en todos los cortes. Si una nota tiene 2 vendedores, cada uno cobra
              su comisión.
            </p>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nota</th>
                  <th>Cliente</th>
                  <th style={{ textAlign: "right" }}>Venta sin IVA</th>
                  <th style={{ textAlign: "right" }}>Comisión</th>
                </tr>
              </thead>
              {corte.comisiones.map((c) => (
                <tbody key={c._id}>
                  <tr style={{ background: "#f1f5f9" }}>
                    <td colSpan={3} style={{ fontWeight: 700 }}>
                      {c.vendedor}
                    </td>
                    <td colSpan={2} align="right">
                      <span style={{ marginRight: 6 }}>% comisión</span>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={porcs[c._id] !== undefined ? porcs[c._id] : c.porc}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPorcs((p) => ({ ...p, [c._id]: v }));
                        }}
                        onBlur={() => guardarPorc(c)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        style={{ width: 90, display: "inline-block", textAlign: "right" }}
                      />
                    </td>
                  </tr>
                  {c.notas.map((n, i) => (
                    <tr key={`${c._id}-${n.numero}-${i}`}>
                      <td>{dmy(n.fecha)}</td>
                      <td>{n.numero}</td>
                      <td>{n.cliente}</td>
                      <td align="right">{fmt(n.base)}</td>
                      <td align="right">{fmt(n.importe)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={3} align="right">
                      Total {c.vendedor}
                    </td>
                    <td align="right">{fmt(c.base)}</td>
                    <td align="right">{fmt(c.importe)}</td>
                  </tr>
                </tbody>
              ))}
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: "2px solid #0f172a" }}>
                  <td colSpan={4} align="right">
                    Total comisiones
                  </td>
                  <td align="right">{fmt(corte.totalComisiones)}</td>
                </tr>
              </tfoot>
            </Table>
            <br />

            <Row>
              {/* Piezas por departamento */}
              <Col md={6}>
                <h5 align="center">Artículos por departamento</h5>
                <Table size="sm" striped>
                  <thead>
                    <tr>
                      <th>Departamento</th>
                      <th style={{ textAlign: "right" }}>Vendidos</th>
                      <th style={{ textAlign: "right" }}>Devueltos</th>
                      <th style={{ textAlign: "right" }}>Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corte.departamentos.map((d) => (
                      <tr key={d.departamento}>
                        <td>{d.departamento}</td>
                        <td align="right">{num(d.vendidos)}</td>
                        <td align="right">{num(d.devueltos)}</td>
                        <td align="right">{num(d.diferencia)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td>Total</td>
                      <td align="right">{num(corte.piezas.vendidos)}</td>
                      <td align="right">{num(corte.piezas.devueltos)}</td>
                      <td align="right">{num(corte.piezas.diferencia)}</td>
                    </tr>
                    <tr>
                      <td colSpan={3}>Precio promedio por pieza</td>
                      <td align="right">{fmt(corte.piezas.precioPromedio)}</td>
                    </tr>
                  </tfoot>
                </Table>
              </Col>
              {/* Gastos del periodo */}
              <Col md={6}>
                <h5 align="center">Gastos</h5>
                <Table size="sm" striped>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Descripción</th>
                      <th style={{ textAlign: "right" }}>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corte.gastos.map((g, i) => (
                      <tr key={i}>
                        <td>{dmy(g.fecha)}</td>
                        <td>{g.concepto}</td>
                        <td>{g.descripcion}</td>
                        <td align="right">{fmt(g.total)}</td>
                      </tr>
                    ))}
                    {!corte.gastos.length && (
                      <tr>
                        <td colSpan={4} align="center" style={{ color: "#64748b" }}>
                          Sin gastos capturados en el periodo
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={3}>Total gastos</td>
                      <td align="right">{fmt(corte.totalGastos)}</td>
                    </tr>
                  </tfoot>
                </Table>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
}

export default CorteDiarioVentas;
